import { Paragraph } from './P.styles';
import React, { CSSProperties, ReactNode } from 'react';

/** <p> tag with props to make common styling easier. */
interface PProps extends React.HTMLAttributes<HTMLParagraphElement> {
    /** Render text in bold. */
    bold?: boolean;
    /** Top margin. If value of 'true' given, standard 30px will be used. Otherwise can be string including unit or a number of px. This prop overrules any top margin set in 'margin' prop. */
    marginTop?: boolean | string | number;
    /** CSS margin value. Numbers will be treated as px. */
    margin?: string | number;
    /** Centre align text. */
    centre?: boolean;
    /** Right align text. */
    right?: boolean;
    /** Text colour. */
    colour?: string;
    /** Font size. Can be CSS string or number of px. */
    fontSize?: string | number;
    /** Children nodes */
    children?: ReactNode;
    /** Additional styles */
    style?: CSSProperties;
}

/**
 * `<P />` is a custom paragraph component that wraps a `<p>` tag, providing props for common styling options.
 * 
 * @remarks
 * Use this component to easily apply styles such as bold text, margin, alignment, color, and font size to paragraphs.
 * 
 * @param props - Props for configuring the paragraph's appearance and behavior.
 * @param props.bold - If true, renders text in bold (fontWeight 300).
 * @param props.marginTop - Top margin. If true, uses 30px; otherwise, accepts a string or number (px).
 * @param props.margin - CSS margin value. Numbers are treated as px.
 * @param props.centre - If true, center-aligns the text.
 * @param props.right - If true, right-aligns the text.
 * @param props.colour - Text color.
 * @param props.fontSize - Font size, as a CSS string or number (px).
 * @param props.children - Content to render inside the paragraph.
 * @param props.style - Additional CSS styles.
 * 
 * @example
 * ```tsx
 * <P bold marginTop="2em" centre colour="#333" fontSize={18}>
 *   This is a styled paragraph.
 * </P>
 * ```
 */
const P: React.FC<PProps> = ({
    bold = false,
    marginTop = false,
    margin,
    centre = false,
    right = false,
    colour,
    fontSize,
    children,
    style: extraStyles = {},
    ...props
}) => {
    const style: CSSProperties = {
        fontWeight: bold ? 300 : 100,
        margin,
        color: colour,
        fontSize,
        textAlign: centre ? 'center' : right ? 'right' : undefined,
        ...extraStyles
    };

    if (marginTop || marginTop === 0) {
        style.marginTop = marginTop === true ? 30 : marginTop;
    }

    return (
        <Paragraph
            style={style}
            {...props}
        >
            {children}
        </Paragraph>
    );
};

export default P;
